import {
  SERVICE_ACTIVATION_TITLE,
  TRACKING_DELIVERY_DETAILS_FAILED,
} from 'features/order-tracker/store/sagas/services/helpers/constants';
import {
  APPOINTMENT_TYPES,
  MILESTONE_STATUSES,
  ORDER_SERVICES_TYPES,
  SERVICE_ACTIVATION_PRODUCTS_TYPES,
} from 'features/order-tracker/utils/entries';

const typePriority = [
  APPOINTMENT_TYPES.broadband,
  APPOINTMENT_TYPES.engineer,
  APPOINTMENT_TYPES.expert,
  ORDER_SERVICES_TYPES.Delivery,
  SERVICE_ACTIVATION_TITLE[SERVICE_ACTIVATION_PRODUCTS_TYPES.BBProduct],
  SERVICE_ACTIVATION_TITLE[SERVICE_ACTIVATION_PRODUCTS_TYPES.CVEProduct],
  SERVICE_ACTIVATION_TITLE[SERVICE_ACTIVATION_PRODUCTS_TYPES.BBAndCVE],
  SERVICE_ACTIVATION_TITLE[SERVICE_ACTIVATION_PRODUCTS_TYPES.nonHalo],
];

const sortMilestonesByProductType = (a: common.OrderMilestone, b: common.OrderMilestone): number => {
  const aIndex =
    a.type === ORDER_SERVICES_TYPES.Delivery
      ? typePriority.indexOf(ORDER_SERVICES_TYPES.Delivery)
      : typePriority.indexOf(a.title);
  const bIndex =
    b.type === ORDER_SERVICES_TYPES.Delivery
      ? typePriority.indexOf(ORDER_SERVICES_TYPES.Delivery)
      : typePriority.indexOf(b.title);

  return aIndex - bIndex;
};

const sortMilestonesByDatesAndTypes = (milestones: common.OrderMilestone[] | []): common.OrderMilestone[] | [] => {
  if (!milestones.length) return [];

  return milestones.sort((a, b) => {
    if (new Date(a.formattedDate!) > new Date(b.formattedDate!)) return 1;
    if (new Date(a.formattedDate!) < new Date(b.formattedDate!)) return -1;

    return sortMilestonesByProductType(a, b);
  });
};

const sortMilestonesByStatus = (milestones: common.OrderMilestone[]): common.SortedByStatusMilestonesType =>
  milestones.reduce(
    (acc, item) => {
      if (!item.formattedDate) return acc;
      switch (item.milestoneStatus) {
        case MILESTONE_STATUSES.complete:
          acc.completedMilestones.push(item);
          return acc;
        case MILESTONE_STATUSES.inProgress:
          acc.inProgressMilestones.push(item);
          return acc;
        case MILESTONE_STATUSES.incomplete:
          acc.incompleteMilestones.push(item);
          return acc;
        default:
          return acc;
      }
    },
    {
      completedMilestones: [] as common.OrderMilestone[],
      inProgressMilestones: [] as common.OrderMilestone[],
      incompleteMilestones: [] as common.OrderMilestone[],
    },
  );

export const sortMilestones = (milestones: common.OrderMilestone[]): common.OrderMilestone[] => {
  const sortedMilestonesByStatus = sortMilestonesByStatus(milestones);
  const isIncompleteOrder = milestones.some(({ milestoneStatus }) => milestoneStatus !== MILESTONE_STATUSES.complete);
  const orderPlaced = milestones.find(({ title }) => title === 'Order placed') as common.OrderMilestone;
  const billing = {
    ...(milestones.find(({ type }) => type === ORDER_SERVICES_TYPES.Billing) as common.OrderMilestone),
    milestoneStatus: isIncompleteOrder ? MILESTONE_STATUSES.incomplete : MILESTONE_STATUSES.complete,
  };
  const deliveriesFailed = milestones.filter(({ status }) => status === 'Delivery details failed');
  const toBeSent = milestones.filter(({ status }) => status === 'To Be Sent');

  const productsMilestones = [
    ...sortMilestonesByDatesAndTypes(sortedMilestonesByStatus.completedMilestones),
    ...sortMilestonesByDatesAndTypes(sortedMilestonesByStatus.inProgressMilestones),
    ...sortMilestonesByDatesAndTypes(sortedMilestonesByStatus.incompleteMilestones),
    ...deliveriesFailed,
    ...toBeSent,
  ];

  const firstDeliveryMilestone = productsMilestones.find(
    ({ type, status }) => type === ORDER_SERVICES_TYPES.Delivery && status !== TRACKING_DELIVERY_DETAILS_FAILED,
  );
  if (firstDeliveryMilestone) {
    firstDeliveryMilestone.isShowLoginBenefits = true;
  }

  return [orderPlaced || { milestoneStatus: MILESTONE_STATUSES.complete }, ...productsMilestones, billing];
};
